import { supabase } from './supabase';

/**
 * Client detail → Google Ads. The account link and lead definition are columns on clients
 * (google_ads_customer_id, google_ads_lead_actions); the daily KPI log is ads_kpi_daily, written by
 * scripts/lib/google-ads.mjs; the journal and pinned state are ads_journal and ads_account_state.
 * Anything that needs the Google Ads API goes through netlify/functions/google-ads.mjs.
 */
export interface AdsAccount {
  id: string;
  name: string;
  status: string;
  currency: string;
}

export interface ConversionAction {
  id: string;
  name: string;
  category: string;
  primary: boolean;
  status: string;
  type: string;
}

export interface KpiRow {
  snapshot_date: string;
  cost: number;
  leads: number;
  cpa: number | null;
  clicks: number;
  impressions: number;
  all_conversions: number;
  source: 'daily' | 'backfill';
}

/** Time frames on Client detail → Google Ads; the chart and the KPI cards share one. */
export type Range = '30' | '90' | '180' | '365' | 'all';

export const RANGES: { id: Range; label: string; long: string }[] = [
  { id: '30', label: '30D', long: '30 days' },
  { id: '90', label: '90D', long: '90 days' },
  { id: '180', label: '6M', long: '6 months' },
  { id: '365', label: '1Y', long: '1 year' },
  { id: 'all', label: 'All', long: 'all time' },
];

export const JOURNAL_KINDS = [
  { id: 'change', label: 'Change', tone: 'accent' },
  { id: 'check_in', label: 'Check-in', tone: 'info' },
  { id: 'decision', label: 'Decision', tone: 'up' },
  { id: 'experiment', label: 'Experiment', tone: 'warn' },
  { id: 'external_change', label: 'External change', tone: 'down' },
  { id: 'tracking_change', label: 'Tracking change', tone: 'down' },
  { id: 'note', label: 'Note', tone: 'neutral' },
] as const;

export type JournalKind = (typeof JOURNAL_KINDS)[number]['id'];

export interface JournalEntry {
  id: string;
  client_id: string;
  entry_date: string;
  kind: JournalKind;
  title: string;
  body: string | null;
  review_by: string | null;
  reviewed_at: string | null;
  created_by: string | null;
  author: string | null;
  created_at: string;
  creator?: { name: string | null } | null;
}

export interface AccountState {
  client_id: string;
  status: string | null;
  goal: string | null;
  next_check: string | null;
  notes: string | null;
  updated_at: string;
  updated_by_name: string | null;
}

export interface AdsLink {
  customerId: string | null;
  leadActions: string[];
  syncAt: string | null;
  syncError: string | null;
}

async function call<T>(body: Record<string, unknown>): Promise<T> {
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  if (!token) throw new Error('not signed in');
  const res = await fetch('/.netlify/functions/google-ads', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const text = await res.text();
  const parsed = (text ? JSON.parse(text) : {}) as T & { error?: string };
  if (!res.ok) throw new Error(parsed.error || text || `google-ads failed: ${res.status}`);
  return parsed;
}

/** Client accounts under the Bullfinch MCC. */
export const fetchAdsAccounts = async () => (await call<{ accounts: AdsAccount[] }>({ action: 'accounts' })).accounts;

export const fetchConversionActions = async (customerId: string) =>
  (await call<{ actions: ConversionAction[] }>({ action: 'conversion_actions', customer_id: customerId })).actions;

/** Log the client's KPIs now. rebuild drops the history first (after the lead definition changes). */
export const syncAdsNow = (clientId: string, rebuild = false) => call<{ written: number }>({ action: 'sync', client_id: clientId, rebuild });

export interface PeriodTotals {
  from: string;
  to: string;
  cost: number;
  leads: number;
  clicks: number;
  impressions: number;
  cpa: number | null;
}

/** Totals for [from, to] and the same span just before it, live from Google (late conversions included). */
export const fetchPeriodTotals = (clientId: string, from: string, to: string, compare = true) =>
  call<{ current: PeriodTotals; previous: PeriodTotals | null }>({ action: 'period', client_id: clientId, from, to, compare });

/** Queue an Account Check-in as the signed-in user. Skipped (not an error) when this client's last one is still open. */
export const queueAdsCheckin = (clientId: string) =>
  call<{ skipped: boolean; job: { id: string; status: string } }>({ action: 'checkin', client_id: clientId });

export async function fetchAdsLink(clientId: string): Promise<AdsLink> {
  const { data, error } = await supabase
    .from('clients')
    .select('google_ads_customer_id, google_ads_lead_actions, google_ads_sync_at, google_ads_sync_error')
    .eq('id', clientId)
    .maybeSingle();
  if (error) throw error;
  return {
    customerId: data?.google_ads_customer_id ?? null,
    leadActions: data?.google_ads_lead_actions ?? [],
    syncAt: data?.google_ads_sync_at ?? null,
    syncError: data?.google_ads_sync_error ?? null,
  };
}

/** Which client each account is already linked to, by customer id. */
export async function fetchLinkedAccounts(): Promise<Map<string, { id: string; name: string }>> {
  const { data, error } = await supabase.from('clients').select('id, name, google_ads_customer_id').not('google_ads_customer_id', 'is', null);
  if (error) throw error;
  return new Map((data ?? []).map((c) => [String(c.google_ads_customer_id), { id: c.id, name: c.name }]));
}

/**
 * Link (or unlink) the account. The lead definition belongs to the old account, so it resets; the caller then
 * runs syncAdsNow(clientId, true), which drops the old history server-side (ads_kpi_daily is read-only here).
 */
export async function setAdsAccount(clientId: string, customerId: string | null): Promise<void> {
  const { error } = await supabase
    .from('clients')
    .update({ google_ads_customer_id: customerId, google_ads_lead_actions: null, google_ads_sync_at: null, google_ads_sync_error: null })
    .eq('id', clientId);
  if (error) throw error;
}

export async function setLeadActions(clientId: string, actionIds: string[]): Promise<void> {
  const { error } = await supabase
    .from('clients')
    .update({ google_ads_lead_actions: actionIds.length ? actionIds : null })
    .eq('id', clientId);
  if (error) throw error;
}

export async function fetchKpis(clientId: string): Promise<KpiRow[]> {
  // Full history runs to thousands of rows (Pete's: back to 2017), past PostgREST's 1,000-row cap, so page.
  const all: KpiRow[] = [];
  for (let from = 0; ; from += 1000) {
    const { data, error } = await supabase
      .from('ads_kpi_daily')
      .select('snapshot_date, cost, leads, cpa, clicks, impressions, all_conversions, source')
      .eq('client_id', clientId)
      .order('snapshot_date', { ascending: true })
      .range(from, from + 999);
    if (error) throw error;
    all.push(
      ...((data ?? []).map((r) => ({
        ...r,
        cost: Number(r.cost),
        leads: Number(r.leads),
        cpa: r.cpa == null ? null : Number(r.cpa),
        clicks: Number(r.clicks),
        impressions: Number(r.impressions),
        all_conversions: Number(r.all_conversions),
      })) as KpiRow[]),
    );
    if (!data || data.length < 1000) return all;
  }
}

export async function fetchJournal(clientId: string): Promise<JournalEntry[]> {
  const { data, error } = await supabase
    .from('ads_journal')
    .select('*, creator:users!ads_journal_created_by_fkey(name)')
    .eq('client_id', clientId)
    .order('entry_date', { ascending: false })
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as JournalEntry[];
}

export type JournalDraft = Pick<JournalEntry, 'entry_date' | 'kind' | 'title' | 'body' | 'review_by'>;

export async function addJournalEntry(clientId: string, draft: JournalDraft, author?: string): Promise<void> {
  const { data } = await supabase.auth.getSession();
  const { error } = await supabase.from('ads_journal').insert({
    client_id: clientId,
    ...draft,
    created_by: author ? null : data.session?.user.id ?? null,
    author: author ?? null,
  });
  if (error) throw error;
}

export async function updateJournalEntry(id: string, patch: Partial<JournalDraft> & { reviewed_at?: string | null }): Promise<void> {
  const { error } = await supabase.from('ads_journal').update({ ...patch, updated_at: new Date().toISOString() }).eq('id', id);
  if (error) throw error;
}

export async function deleteJournalEntry(id: string): Promise<void> {
  const { error } = await supabase.from('ads_journal').delete().eq('id', id);
  if (error) throw error;
}

export async function fetchAccountState(clientId: string): Promise<AccountState | null> {
  const { data, error } = await supabase.from('ads_account_state').select('*').eq('client_id', clientId).maybeSingle();
  if (error) throw error;
  return data as AccountState | null;
}

export async function saveAccountState(
  clientId: string,
  state: Pick<AccountState, 'status' | 'goal' | 'next_check' | 'notes'>,
  userName: string | null,
): Promise<void> {
  const { data } = await supabase.auth.getSession();
  const { error } = await supabase.from('ads_account_state').upsert({
    client_id: clientId,
    ...state,
    updated_at: new Date().toISOString(),
    updated_by: data.session?.user.id ?? null,
    updated_by_name: userName,
  });
  if (error) throw error;
}

export const fmtMoney = (n: number | null | undefined, digits = 0) =>
  n == null ? '—' : `$${n.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })}`;

export const fmtCount = (n: number | null | undefined) =>
  n == null ? '—' : n.toLocaleString('en-US', { maximumFractionDigits: Number.isInteger(n) ? 0 : 1 });

/** "4449048007" → "444-904-8007", the way Google Ads shows it. */
export const fmtCustomerId = (id: string) => id.replace(/^(\d{3})(\d{3})(\d{4})$/, '$1-$2-$3');
